import { useEffect, useMemo, useRef, useState } from "react";
import {
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  LineStyle,
  createChart,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type MouseEventParams,
  type Time,
} from "lightweight-charts";

import type { ChartBar } from "@/services/adapters/chart-adapter";
import type { WyckoffMarker } from "@/features/wyckoff/local-analysis";

interface LinePoint {
  time: string;
  value: number;
}

interface WyckoffChartProps {
  bars: ChartBar[];
  datasetKey?: string;
  trHighSeries: LinePoint[];
  trLowSeries: LinePoint[];
  markers: WyckoffMarker[];
  segments?: Array<{
    phase: "accumulation" | "distribution" | "neutral";
    start_time: string;
    end_time: string;
    top: number;
    bottom: number;
  }>;
  activeEventTime?: string | null;
  subPane: "vol" | "atr" | "rsi";
  mainPaneRatio?: number;
  onMainPaneRatioChange?: (ratio: number) => void;
  onMarkerSelect?: (time: string | null) => void;
  onVisibleRangeChange?: (range: { from: string; to: string } | null) => void;
  onCrosshairBarChange?: (bar: { time: string; open: number; high: number; low: number; close: number } | null) => void;
}

interface Runtime {
  mainChart: IChartApi;
  volumeChart: IChartApi;
  candleSeries: ISeriesApi<"Candlestick">;
  volumeSeries: ISeriesApi<"Histogram">;
  atrSeries: ISeriesApi<"Line">;
  rsiSeries: ISeriesApi<"Line">;
  trHighSeries: ISeriesApi<"Line">;
  trLowSeries: ISeriesApi<"Line">;
  markerPlugin: ReturnType<typeof createSeriesMarkers<any>>;
  activeMarkerPlugin: ReturnType<typeof createSeriesMarkers<any>>;
  unsubscribeRange?: () => void;
}

interface RenderPhaseBox {
  key: string;
  phase: "accumulation" | "distribution" | "neutral";
  label: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

export function WyckoffChart({
  bars,
  datasetKey,
  trHighSeries,
  trLowSeries,
  markers,
  segments = [],
  activeEventTime = null,
  subPane,
  mainPaneRatio = 0.76,
  onMainPaneRatioChange,
  onMarkerSelect,
  onVisibleRangeChange,
  onCrosshairBarChange,
}: WyckoffChartProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const volumeRef = useRef<HTMLDivElement | null>(null);
  const runtimeRef = useRef<Runtime | null>(null);
  const syncingRangeRef = useRef(false);
  const onVisibleRangeChangeRef = useRef<WyckoffChartProps["onVisibleRangeChange"]>(onVisibleRangeChange);
  const onMainPaneRatioChangeRef = useRef<WyckoffChartProps["onMainPaneRatioChange"]>(onMainPaneRatioChange);
  const onMarkerSelectRef = useRef<WyckoffChartProps["onMarkerSelect"]>(onMarkerSelect);
  const onCrosshairBarChangeRef = useRef<WyckoffChartProps["onCrosshairBarChange"]>(onCrosshairBarChange);
  const phaseSegmentsRef = useRef<Array<{
    phase: "accumulation" | "distribution" | "neutral";
    start_time: string;
    end_time: string;
    top: number;
    bottom: number;
  }>>([]);
  const recomputePhaseBoxesRef = useRef<(() => void) | null>(null);
  const paneDraggingRef = useRef(false);
  const markerTimeSetRef = useRef<Set<string>>(new Set());
  const barsByTimeRef = useRef<Map<string, { time: string; open: number; high: number; low: number; close: number }>>(new Map());
  const fittedDatasetKeyRef = useRef<string | null>(null);
  const fittedOnceRef = useRef(false);
  const [phaseBoxes, setPhaseBoxes] = useState<RenderPhaseBox[]>([]);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const normalizedBars = useMemo(
    () =>
      bars
        .map((bar) => ({
          ...bar,
          time: normalizeTime(bar.time),
        }))
        .filter((bar) => bar.time),
    [bars],
  );
  const normalizedMarkers = useMemo(
    () =>
      markers
        .map((marker) => ({
          ...marker,
          time: normalizeTime(marker.time),
        }))
        .filter((marker) => marker.time),
    [markers],
  );
  const normalizedSegments = useMemo(
    () =>
      segments
        .map((segment) => ({
          ...segment,
          start_time: normalizeTime(segment.start_time),
          end_time: normalizeTime(segment.end_time),
          top: Number(segment.top),
          bottom: Number(segment.bottom),
        }))
        .filter(
          (segment) =>
            Boolean(segment.start_time) &&
            Boolean(segment.end_time) &&
            Number.isFinite(segment.top) &&
            Number.isFinite(segment.bottom) &&
            segment.top > segment.bottom,
        ),
    [segments],
  );
  const normalizedActiveEventTime = useMemo(() => normalizeTime(activeEventTime), [activeEventTime]);
  const atrSeries = useMemo(() => buildAtrSeries(normalizedBars, 14), [normalizedBars]);
  const rsiSeries = useMemo(() => buildRsiSeries(normalizedBars, 14), [normalizedBars]);
  const subPaneLabel = subPane.toUpperCase();
  const safeMainPaneRatio = clampNumber(mainPaneRatio, 0.6, 0.86);

  useEffect(() => {
    onVisibleRangeChangeRef.current = onVisibleRangeChange;
  }, [onVisibleRangeChange]);

  useEffect(() => {
    onMainPaneRatioChangeRef.current = onMainPaneRatioChange;
  }, [onMainPaneRatioChange]);

  useEffect(() => {
    onMarkerSelectRef.current = onMarkerSelect;
  }, [onMarkerSelect]);

  useEffect(() => {
    onCrosshairBarChangeRef.current = onCrosshairBarChange;
  }, [onCrosshairBarChange]);

  useEffect(() => {
    barsByTimeRef.current = new Map(
      normalizedBars.map((bar) => [
        bar.time,
        {
          time: bar.time,
          open: bar.open,
          high: bar.high,
          low: bar.low,
          close: bar.close,
        },
      ]),
    );
  }, [normalizedBars]);

  useEffect(() => {
    phaseSegmentsRef.current = normalizedSegments;
    recomputePhaseBoxesRef.current?.();
  }, [normalizedSegments]);

  useEffect(() => {
    markerTimeSetRef.current = new Set(normalizedMarkers.map((item) => item.time));
  }, [normalizedMarkers]);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      if (!paneDraggingRef.current || !rootRef.current) {
        return;
      }
      const rect = rootRef.current.getBoundingClientRect();
      if (rect.height <= 0) {
        return;
      }
      const y = event.clientY - rect.top;
      const nextRatio = clampNumber(y / rect.height, 0.6, 0.86);
      onMainPaneRatioChangeRef.current?.(nextRatio);
    };
    const onMouseUp = () => {
      paneDraggingRef.current = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const width = Math.floor(entry.contentRect.width);
      const height = Math.floor(entry.contentRect.height);
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    });
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mainRef.current || !volumeRef.current) {
      return;
    }

    const mainChart = createChart(mainRef.current, {
      width: Math.max(mainRef.current.clientWidth, 60),
      height: Math.max(mainRef.current.clientHeight, 120),
      layout: {
        background: { type: ColorType.Solid, color: "#0f1115" },
        textColor: "#cfd6df",
        attributionLogo: false,
      },
      grid: {
        horzLines: { color: "rgba(255,255,255,0.05)" },
        vertLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: {
        borderColor: "rgba(255,255,255,0.1)",
        minimumWidth: 64,
      },
      leftPriceScale: { visible: false },
      timeScale: {
        borderColor: "rgba(255,255,255,0.08)",
        rightOffset: 8,
        minBarSpacing: 0.4,
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: true, axisPressedMouseMove: true, pinch: false },
    });

    const volumeChart = createChart(volumeRef.current, {
      width: Math.max(volumeRef.current.clientWidth, 60),
      height: Math.max(volumeRef.current.clientHeight, 72),
      layout: {
        background: { type: ColorType.Solid, color: "#0f1115" },
        textColor: "#8b96a6",
        attributionLogo: false,
      },
      grid: {
        horzLines: { color: "rgba(255,255,255,0.05)" },
        vertLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: {
        borderColor: "rgba(255,255,255,0.1)",
        minimumWidth: 64,
      },
      leftPriceScale: { visible: false },
      timeScale: {
        borderColor: "rgba(255,255,255,0.08)",
        rightOffset: 8,
        minBarSpacing: 0.4,
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: true, axisPressedMouseMove: true, pinch: false },
    });

    const candleSeries = mainChart.addSeries(CandlestickSeries, {
      upColor: "#f6465d",
      downColor: "#16c784",
      wickUpColor: "#f6465d",
      wickDownColor: "#16c784",
      borderVisible: false,
    });
    const trHigh = mainChart.addSeries(LineSeries, {
      color: "rgba(56, 189, 248, 0.78)",
      lineStyle: LineStyle.Dashed,
      lineWidth: 1,
      crosshairMarkerVisible: false,
    });
    const trLow = mainChart.addSeries(LineSeries, {
      color: "rgba(244, 114, 182, 0.75)",
      lineStyle: LineStyle.Dashed,
      lineWidth: 1,
      crosshairMarkerVisible: false,
    });
    const volumeSeries = volumeChart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceLineVisible: false,
    });
    const atrSeries = volumeChart.addSeries(LineSeries, {
      color: "#f59e0b",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      crosshairMarkerVisible: false,
    });
    const rsiSeries = volumeChart.addSeries(LineSeries, {
      color: "#60a5fa",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      crosshairMarkerVisible: false,
    });
    const markerPlugin = createSeriesMarkers(candleSeries, []);
    const activeMarkerPlugin = createSeriesMarkers(candleSeries, []);

    const syncMain = (range: { from: number; to: number } | null) => {
      if (!range || syncingRangeRef.current) {
        return;
      }
      syncingRangeRef.current = true;
      volumeChart.timeScale().setVisibleLogicalRange(range);
      syncingRangeRef.current = false;
      recomputePhaseBoxesRef.current?.();
    };
    const syncVolume = (range: { from: number; to: number } | null) => {
      if (!range || syncingRangeRef.current) {
        return;
      }
      syncingRangeRef.current = true;
      mainChart.timeScale().setVisibleLogicalRange(range);
      syncingRangeRef.current = false;
    };
    mainChart.timeScale().subscribeVisibleLogicalRangeChange(syncMain);
    volumeChart.timeScale().subscribeVisibleLogicalRangeChange(syncVolume);
    recomputePhaseBoxesRef.current = () => {
      if (!mainRef.current) {
        setPhaseBoxes([]);
        return;
      }
      const width = Math.max(mainRef.current.clientWidth, 0);
      const height = Math.max(mainRef.current.clientHeight, 0);
      if (width <= 0 || height <= 0) {
        setPhaseBoxes([]);
        return;
      }
      setPhaseBoxes(buildPhaseBoxes(mainChart, candleSeries, phaseSegmentsRef.current, width, height));
    };
    const visibleRangeHandler = (range: { from: unknown; to: unknown } | null) => {
      recomputePhaseBoxesRef.current?.();
      const callback = onVisibleRangeChangeRef.current;
      if (!callback) {
        return;
      }
      if (!range) {
        callback(null);
        return;
      }
      const from = normalizeTime(range.from);
      const to = normalizeTime(range.to);
      if (!from || !to) {
        callback(null);
        return;
      }
      callback({ from, to });
    };
    mainChart.timeScale().subscribeVisibleTimeRangeChange(visibleRangeHandler);
    const onClickHandler = (param: MouseEventParams<Time>) => {
      const callback = onMarkerSelectRef.current;
      if (!callback) {
        return;
      }
      if (!param.time) {
        callback(null);
        return;
      }
      const timeKey = normalizeTime(param.time);
      if (!timeKey || !markerTimeSetRef.current.has(timeKey)) {
        return;
      }
      callback(timeKey);
    };
    mainChart.subscribeClick(onClickHandler);
    const onCrosshairMoveHandler = (param: MouseEventParams<Time>) => {
      const callback = onCrosshairBarChangeRef.current;
      if (!callback) {
        return;
      }
      const timeKey = normalizeTime(param.time);
      if (!timeKey) {
        callback(null);
        return;
      }
      const seriesEntry = (param.seriesData.get(candleSeries) || null) as
        | {
            open?: unknown;
            high?: unknown;
            low?: unknown;
            close?: unknown;
          }
        | null;
      const open = Number(seriesEntry?.open);
      const high = Number(seriesEntry?.high);
      const low = Number(seriesEntry?.low);
      const close = Number(seriesEntry?.close);
      if (Number.isFinite(open) && Number.isFinite(high) && Number.isFinite(low) && Number.isFinite(close)) {
        callback({ time: timeKey, open, high, low, close });
        return;
      }
      callback(barsByTimeRef.current.get(timeKey) || null);
    };
    mainChart.subscribeCrosshairMove(onCrosshairMoveHandler);

    runtimeRef.current = {
      mainChart,
      volumeChart,
      candleSeries,
      volumeSeries,
      atrSeries,
      rsiSeries,
      trHighSeries: trHigh,
      trLowSeries: trLow,
      markerPlugin,
      activeMarkerPlugin,
      unsubscribeRange: () => mainChart.timeScale().unsubscribeVisibleTimeRangeChange(visibleRangeHandler),
    };
    recomputePhaseBoxesRef.current?.();

    return () => {
      mainChart.timeScale().unsubscribeVisibleLogicalRangeChange(syncMain);
      volumeChart.timeScale().unsubscribeVisibleLogicalRangeChange(syncVolume);
      runtimeRef.current?.unsubscribeRange?.();
      mainChart.unsubscribeClick(onClickHandler);
      mainChart.unsubscribeCrosshairMove(onCrosshairMoveHandler);
      markerPlugin.detach();
      activeMarkerPlugin.detach();
      mainChart.remove();
      volumeChart.remove();
      recomputePhaseBoxesRef.current = null;
      setPhaseBoxes([]);
      runtimeRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!runtimeRef.current || !mainRef.current || !volumeRef.current) {
      return;
    }
    const { mainChart, volumeChart } = runtimeRef.current;
    const width = Math.max(size.width, 60);
    const mainHeight = Math.max(120, Math.floor(mainRef.current.clientHeight || 120));
    const volumeHeight = Math.max(72, Math.floor(volumeRef.current.clientHeight || 72));
    mainChart.resize(width, mainHeight);
    volumeChart.resize(width, volumeHeight);
    recomputePhaseBoxesRef.current?.();
  }, [safeMainPaneRatio, size]);

  useEffect(() => {
    if (!runtimeRef.current) {
      return;
    }
    const runtime = runtimeRef.current;
    runtime.candleSeries.setData(
      normalizedBars.map((bar) => ({
        time: bar.time,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
      })),
    );
    runtime.volumeSeries.setData(
      subPane === "vol"
        ? normalizedBars.map((bar) => ({
            time: bar.time,
            value: bar.volume,
            color: bar.close >= bar.open ? "rgba(246, 70, 93, 0.78)" : "rgba(22, 199, 132, 0.78)",
          }))
        : [],
    );
    runtime.atrSeries.setData(subPane === "atr" ? atrSeries : []);
    runtime.rsiSeries.setData(subPane === "rsi" ? rsiSeries : []);
    runtime.trHighSeries.setData(
      trHighSeries
        .map((item) => ({ time: normalizeTime(item.time), value: item.value }))
        .filter((item) => item.time && Number.isFinite(item.value)),
    );
    runtime.trLowSeries.setData(
      trLowSeries
        .map((item) => ({ time: normalizeTime(item.time), value: item.value }))
        .filter((item) => item.time && Number.isFinite(item.value)),
    );
    runtime.markerPlugin.setMarkers(
      normalizedMarkers.map((marker) => ({
        time: marker.time,
        position: marker.position,
        color: marker.color,
        shape: marker.shape,
        text: marker.text,
      })),
    );
    const activeMarker = normalizedActiveEventTime
      ? normalizedMarkers.find((marker) => marker.time === normalizedActiveEventTime)
      : undefined;
    runtime.activeMarkerPlugin.setMarkers(
      normalizedActiveEventTime
        ? [
            {
              time: normalizedActiveEventTime,
              position: activeMarker?.position || "aboveBar",
              color: "#f59e0b",
              shape: "circle",
              text: activeMarker?.text ? `★${activeMarker.text}` : "★",
            },
          ]
        : [],
    );
    const shouldRefit =
      normalizedBars.length > 5 &&
      (datasetKey ? fittedDatasetKeyRef.current !== datasetKey : !fittedOnceRef.current);
    if (shouldRefit) {
      runtime.mainChart.timeScale().fitContent();
      runtime.volumeChart.timeScale().fitContent();
      fittedOnceRef.current = true;
      if (datasetKey) {
        fittedDatasetKeyRef.current = datasetKey;
      }
    }
    recomputePhaseBoxesRef.current?.();
  }, [atrSeries, datasetKey, normalizedActiveEventTime, normalizedBars, normalizedMarkers, rsiSeries, subPane, trHighSeries, trLowSeries]);

  return (
    <div ref={rootRef} className="flex h-full min-h-0 flex-col bg-[#0f1115]">
      <div className="relative min-h-[220px]" style={{ height: `${(safeMainPaneRatio * 100).toFixed(2)}%` }}>
        <div className="pointer-events-none absolute left-3 top-2 z-10 rounded bg-black/45 px-2 py-0.5 text-[10px] font-medium tracking-wide text-slate-400">
          价格与阶段区间
        </div>
        <div className="pointer-events-none absolute inset-0 z-[6]">
          {phaseBoxes.map((box) => {
            const styleToken = getPhaseBoxStyle(box.phase);
            return (
              <div
                key={box.key}
                className="absolute rounded-sm border"
                style={{
                  left: `${box.left}px`,
                  top: `${box.top}px`,
                  width: `${box.width}px`,
                  height: `${box.height}px`,
                  borderColor: styleToken.border,
                  backgroundColor: styleToken.background,
                }}
              >
                <span
                  className="absolute left-1 top-1 rounded px-1 py-0.5 text-[9px] font-medium tracking-wide"
                  style={{
                    color: styleToken.text,
                    backgroundColor: styleToken.labelBg,
                  }}
                >
                  {box.label}
                </span>
              </div>
            );
          })}
        </div>
        <div ref={mainRef} className="h-full w-full" />
      </div>
      <div
        className="h-1.5 cursor-row-resize border-y border-[var(--border)] bg-[#111824] hover:bg-[#1b2636]"
        onMouseDown={() => {
          paneDraggingRef.current = true;
          document.body.style.userSelect = "none";
          document.body.style.cursor = "row-resize";
        }}
      />
      <div className="relative min-h-[90px] flex-1">
        <div className="pointer-events-none absolute left-3 top-2 z-10 rounded bg-black/45 px-2 py-0.5 text-[10px] font-medium tracking-wide text-slate-400">
          {subPaneLabel}
        </div>
        <div ref={volumeRef} className="h-full w-full" />
      </div>
    </div>
  );
}

function buildPhaseBoxes(
  chart: IChartApi,
  candleSeries: ISeriesApi<"Candlestick">,
  segments: Array<{
    phase: "accumulation" | "distribution" | "neutral";
    start_time: string;
    end_time: string;
    top: number;
    bottom: number;
  }>,
  width: number,
  height: number,
): RenderPhaseBox[] {
  if (!segments.length || width <= 0 || height <= 0) {
    return [];
  }
  const boxes: RenderPhaseBox[] = [];
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    const xStart = chart.timeScale().timeToCoordinate(segment.start_time);
    const xEnd = chart.timeScale().timeToCoordinate(segment.end_time);
    const yTop = candleSeries.priceToCoordinate(segment.top);
    const yBottom = candleSeries.priceToCoordinate(segment.bottom);
    if (xStart == null || xEnd == null || yTop == null || yBottom == null) {
      continue;
    }
    const leftRaw = Math.min(xStart, xEnd);
    const rightRaw = Math.max(xStart, xEnd);
    if (rightRaw < 0 || leftRaw > width) {
      continue;
    }
    const topRaw = Math.min(yTop, yBottom);
    const bottomRaw = Math.max(yTop, yBottom);
    if (bottomRaw < 0 || topRaw > height) {
      continue;
    }
    const left = clampNumber(leftRaw, 0, width - 1);
    const right = clampNumber(rightRaw, 0, width - 1);
    const top = clampNumber(topRaw, 0, height - 1);
    const bottom = clampNumber(bottomRaw, 0, height - 1);
    boxes.push({
      key: `${segment.start_time}-${segment.end_time}-${segment.phase}-${index}`,
      phase: segment.phase,
      label: segment.phase === "accumulation" ? "吸筹" : segment.phase === "distribution" ? "派发" : "中性",
      left,
      top,
      width: Math.max(3, right - left + 2),
      height: Math.max(3, bottom - top + 2),
    });
  }
  return boxes;
}

function clampNumber(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function getPhaseBoxStyle(phase: "accumulation" | "distribution" | "neutral") {
  if (phase === "accumulation") {
    return {
      border: "rgba(16, 185, 129, 0.65)",
      background: "rgba(16, 185, 129, 0.12)",
      text: "#bbf7d0",
      labelBg: "rgba(6, 78, 59, 0.75)",
    };
  }
  if (phase === "distribution") {
    return {
      border: "rgba(244, 63, 94, 0.65)",
      background: "rgba(244, 63, 94, 0.1)",
      text: "#fecdd3",
      labelBg: "rgba(136, 19, 55, 0.75)",
    };
  }
  return {
    border: "rgba(148, 163, 184, 0.5)",
    background: "rgba(148, 163, 184, 0.08)",
    text: "#e2e8f0",
    labelBg: "rgba(51, 65, 85, 0.75)",
  };
}

function normalizeTime(raw: unknown) {
  if (raw && typeof raw === "object" && "year" in raw && "month" in raw && "day" in raw) {
    const typed = raw as { year: number; month: number; day: number };
    return `${typed.year}-${String(typed.month).padStart(2, "0")}-${String(typed.day).padStart(2, "0")}`;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const date = new Date(raw * 1000);
    if (!Number.isNaN(date.getTime())) {
      const yyyy = date.getUTCFullYear();
      const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(date.getUTCDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  const value = String(raw || "");
  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }
  return value;
}

function buildAtrSeries(bars: ChartBar[], period: number): LinePoint[] {
  if (!bars.length || period < 2) {
    return [];
  }
  const trValues: number[] = [];
  for (let i = 0; i < bars.length; i += 1) {
    const bar = bars[i];
    if (i === 0) {
      trValues.push(Math.max(0, bar.high - bar.low));
      continue;
    }
    const prevClose = bars[i - 1].close;
    const tr = Math.max(bar.high - bar.low, Math.abs(bar.high - prevClose), Math.abs(bar.low - prevClose));
    trValues.push(Math.max(0, tr));
  }
  const result: LinePoint[] = [];
  let prevAtr: number | null = null;
  for (let i = 0; i < trValues.length; i += 1) {
    if (i < period - 1) {
      continue;
    }
    if (prevAtr == null) {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j += 1) {
        sum += trValues[j] || 0;
      }
      prevAtr = sum / period;
    } else {
      prevAtr = (prevAtr * (period - 1) + (trValues[i] || 0)) / period;
    }
    result.push({ time: bars[i].time, value: prevAtr });
  }
  return result;
}

function buildRsiSeries(bars: ChartBar[], period: number): LinePoint[] {
  if (bars.length < period + 1 || period < 2) {
    return [];
  }
  const gains: number[] = [];
  const losses: number[] = [];
  for (let i = 1; i < bars.length; i += 1) {
    const delta = bars[i].close - bars[i - 1].close;
    gains.push(Math.max(0, delta));
    losses.push(Math.max(0, -delta));
  }
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 0; i < period; i += 1) {
    avgGain += gains[i] || 0;
    avgLoss += losses[i] || 0;
  }
  avgGain /= period;
  avgLoss /= period;
  const result: LinePoint[] = [];
  for (let i = period; i < gains.length; i += 1) {
    avgGain = ((avgGain * (period - 1)) + (gains[i] || 0)) / period;
    avgLoss = ((avgLoss * (period - 1)) + (losses[i] || 0)) / period;
    const rs = avgLoss <= 0 ? 100 : avgGain / avgLoss;
    const rsi = avgLoss <= 0 ? 100 : 100 - (100 / (1 + rs));
    result.push({ time: bars[i + 1].time, value: Math.max(0, Math.min(100, rsi)) });
  }
  return result;
}
